import { ContentSeries, Course } from '@/client/courses';
import {
  PlannerCoursesFiltersType,
  PlannerCRUDCourse,
  PlannerType,
} from '@/client/planner/types';
import { DateFormats } from '@/system-settings/enums';
import moment from 'moment';
import {
  convertPeriodStringToDays,
  parsePeriodStringToUserReadable,
} from '../helpers';

export const getDateKey = (
  item: PlannerCRUDCourse,
  isOnboardingPlan: boolean,
): string => {
  if (isOnboardingPlan) {
    return parsePeriodStringToUserReadable(item.periodValue, item.periodUnit);
  } else {
    const itemDate = item.enrollImmediately
      ? moment()
      : moment(item?.enrollDate)?.startOf('day') || moment();
    return itemDate.format(DateFormats.DATEONLY_SHORT);
  }
};

export const getIsFutureOrToday = (item: PlannerCRUDCourse): boolean => {
  const itemDate = moment(item?.enrollDate)?.startOf('day') || moment();
  return itemDate?.isSameOrAfter(moment().startOf('day'));
};

export const isSameDateAsToday = (item: PlannerCRUDCourse) => {
  const itemDate =
    moment(item?.enrollDate)?.startOf('day') || moment().startOf('day');
  const sameDate = moment(itemDate).isSame(moment(), 'day');
  return sameDate;
};

export const getSortedCoursesInPlanner = (
  items: PlannerCRUDCourse[],
  isOnboardingPlan: boolean,
) => {
  const sortedItems: PlannerCRUDCourse[] = [...items];
  const allItemsHavePosition = sortedItems.every(
    (item) => item.positionInPlanner !== undefined,
  );

  if (isOnboardingPlan) {
    sortedItems.sort((a, b) => {
      const aDays = convertPeriodStringToDays(a.periodValue, a.periodUnit);
      const bDays = convertPeriodStringToDays(b.periodValue, b.periodUnit);

      const dateDiff = aDays - bDays;
      if (dateDiff !== 0 || !allItemsHavePosition) {
        return dateDiff;
      }

      return (a.positionInPlanner || 0) - (b.positionInPlanner || 0);
    });
  } else {
    sortedItems.map((x) =>
      x.plannerScheduleDate &&
      !x.enrollDate &&
      moment(x.plannerScheduleDate).isAfter(moment(), 'days')
        ? (x.enrollDate = moment
            .utc(x.plannerScheduleDate)
            .set({ h: 9 })
            .toDate())
        : x,
    );

    sortedItems.sort((a, b) => {
      const aDate = a.enrollImmediately ? moment() : moment(a.enrollDate);
      const bDate = b.enrollImmediately ? moment() : moment(b.enrollDate);
      const dateDiff = aDate?.diff(bDate) || 0;
      if (dateDiff !== 0 || !allItemsHavePosition) {
        return dateDiff;
      }

      return (a.positionInPlanner || 0) - (b.positionInPlanner || 0);
    });
  }

  return sortedItems;
};

export const updateTimeline = (
  items: PlannerCRUDCourse[],
  setTimelineItems: any,
  isOnboardingPlan?: boolean,
) => {
  const sortedItems: PlannerCRUDCourse[] = getSortedCoursesInPlanner(
    items,
    !!isOnboardingPlan,
  );
  const timelineItems: PlannerCRUDCourse[] = [];
  let lastDate: string;
  let firstNonPastDateIndex: number | null = null;
  let lastItemIndexForCurrentDate: number | null = null;

  sortedItems.forEach((item, index) => {
    const dateKey = getDateKey(item, !!isOnboardingPlan);
    const isFutureOrToday = getIsFutureOrToday(item);

    if (dateKey !== lastDate) {
      if (lastItemIndexForCurrentDate !== null) {
        timelineItems[lastItemIndexForCurrentDate].isLastOfDateGroup = true;
      }

      timelineItems.push({
        ...item,
        isPastDate: !!item.executedAt,
        isDateHeader: true,
        dateKey,
        isLastOfDateGroup: false,
      });
      lastDate = dateKey;
      lastItemIndexForCurrentDate = timelineItems.length - 1;
    } else {
      timelineItems.push({
        ...item,
        isPastDate: !!item.executedAt,
        isDateHeader: false,
        isLastOfDateGroup: false,
      });
      lastItemIndexForCurrentDate = timelineItems.length - 1;
    }

    if (isFutureOrToday && firstNonPastDateIndex === null && !item.executedAt) {
      firstNonPastDateIndex = index;
    }
  });

  if (lastItemIndexForCurrentDate !== null) {
    timelineItems[lastItemIndexForCurrentDate].isLastOfDateGroup = true;
  }

  if (firstNonPastDateIndex !== null) {
    timelineItems[firstNonPastDateIndex].firstNonPastDate = true;
  }

  setTimelineItems(timelineItems);

  return firstNonPastDateIndex;
};

export const getPlannerCoursesByEnrollType = (
  plannerType: PlannerType,
  newUsersSelectedCourses: PlannerCRUDCourse[],
  existingUsersSelectedCourses: PlannerCRUDCourse[],
) => {
  return plannerType === PlannerType.ONBOARDING_PLAN
    ? newUsersSelectedCourses
    : existingUsersSelectedCourses;
};

// Helper function since the filtering is handled in the frontend ( we load all the courses in once )
export const applyPlannerCoursesFilters = (
  courseList: Course[],
  filters: PlannerCoursesFiltersType,
): Course[] => {
  return courseList.filter((course) => {
    if (filters.tags && filters.tags.length > 0) {
      const tagIds = filters.tags
        .map((tag) => {
          if (typeof tag === 'string') {
            return tag;
          }
          if (tag && typeof tag === 'object' && 'id' in tag) {
            return tag.id;
          }
          return null;
        })
        .filter((id): id is string => id !== null);

      const hasMatchingTag = course.tags?.some((tag) =>
        tagIds.includes(tag.id),
      );
      if (!hasMatchingTag) return false;
    }

    if (filters.series && filters.series.length > 0) {
      const seriesValues = filters.series.map((s) => {
        if (s && typeof s === 'object' && 'value' in s) {
          return (s as { value: ContentSeries | null }).value;
        }
        return s;
      });

      const hasNullFilter = seriesValues.some(
        (s) => s === null || s === undefined,
      );

      const seriesFilters = seriesValues.filter(
        (s): s is ContentSeries => s !== null && s !== undefined,
      );

      const courseSeries = course.contentSeries;
      const courseHasNoSeries =
        courseSeries === null || courseSeries === undefined;

      const courseMatchesSeries =
        !courseHasNoSeries &&
        seriesFilters.length > 0 &&
        seriesFilters.includes(courseSeries as ContentSeries);

      if (hasNullFilter && seriesFilters.length > 0) {
        if (courseHasNoSeries || courseMatchesSeries) {
          // Pass - continue to next filter
        } else {
          return false;
        }
      } else if (hasNullFilter) {
        if (!courseHasNoSeries) {
          return false;
        }
      } else {
        if (courseHasNoSeries || !courseMatchesSeries) {
          return false;
        }
      }
    }

    if (filters.courseLanguages && filters.courseLanguages.length > 0) {
      const courseLanguages = course.courseLanguages;
      if (!courseLanguages || courseLanguages.length === 0) {
        return false;
      }

      const hasAllLanguages = filters.courseLanguages.every((filterLang) =>
        courseLanguages.includes(filterLang),
      );
      if (!hasAllLanguages) {
        return false;
      }
    }

    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      const matchesName = course.name?.toLowerCase().includes(searchLower);
      if (!matchesName) return false;
    }

    return true;
  });
};
